import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder } from '@angular/forms';

import { race, take } from 'rxjs';

import { InfoboxButton, InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import {
  TellentAccessService,
  TellentAppsDataFacade,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  EMPLOYEE_ROLE,
  LoginMethod,
  MEMBER_LISTED_ONLY,
  SeatKind,
  SeatType,
  TeamMemberStatus,
  TellentDevFlag,
  TellentMember,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
} from '@core/tellent-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  HeadingComponent,
  IconComponent,
} from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import type { EditMemberForm } from './edit-member-permissions-access.service';
import { EditMemberPermissionsAccessService } from './edit-member-permissions-access.service';
import { EditMemberPermissionsBoxComponent } from './edit-member-permissions-box/edit-member-permissions-box.component';
import { InviteMemberWithoutAccessBoxComponent } from './invite-member-without-access-box/invite-member-without-access-box.component';
import { AccessRolesTableComponent } from '../../access-roles-table/access-roles-table.component';
import { AccessRolesTableService } from '../../access-roles-table/access-roles-table.service';
import { GiveAdminPermissionsDialogComponent } from '../../give-admin-permissions-dialog/give-admin-permissions-dialog.component';
import { EditMembersActionsService } from '../edit-member-header/edit-members-actions.service';

export type SeatTypeValue = {
  toggle: boolean | null;
  appGuid: string | null;
  roleExternalId: string | null;
};

/*
 eslint-disable
  @typescript-eslint/consistent-type-assertions,
 @angular-eslint/no-inputs-metadata-property,
*/

@Component({
  selector: 'edit-member-permissions-access-tab',
  imports: [
    ButtonComponent,
    DialogFooterComponent,
    TranslatePipe,
    DialogBodyComponent,
    ButtonLoadingDirective,
    EditMemberPermissionsBoxComponent,
    HeadingComponent,
    InviteMemberWithoutAccessBoxComponent,
    InfoboxComponent,
    AccessRolesTableComponent,
    IconComponent,
  ],

  templateUrl: './edit-member-permissions-access-tab.component.html',
  styleUrls: ['./edit-member-permissions-access-tab.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  inputs: EditMemberPermissionsAccessTabComponent.NgInputs,
})
export class EditMemberPermissionsAccessTabComponent {
  public static NgInputs = ['memberGuid' as const];

  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly editMemberAccessService = inject(EditMemberPermissionsAccessService);
  private readonly appsDataFacade = inject(TellentAppsDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly accessRolesTableSrv = inject(AccessRolesTableService);
  private readonly editMembersActionsService = inject(EditMembersActionsService);
  private readonly t = injectTranslate();

  protected readonly TeamMemberStatus = TeamMemberStatus;
  protected readonly usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);
  protected readonly canManageTellent = inject(TellentAccessService).canManageTellent;

  public memberGuid: string;
  public destroyRef = inject(DestroyRef);
  public cdRef = inject(ChangeDetectorRef);

  public protectedControls = useProtectedControlsCallback();

  public pending = signal<boolean>(false);

  public memberData: Signal<TellentMember | null>;

  public tellentApps = this.appsDataFacade.entities;

  public formGroup: FormGroup<EditMemberForm>;

  public memberNotInvited = computed(
    () => this.memberData()?.status === TeamMemberStatus.NotInvited,
  );

  public memberHasNoKiwiSeat = computed(() => {
    const seat = this.memberData()!.seats.find(seat => seat.app.kind === SeatKind.CoreHR);
    return !!seat;
  });

  public loginMethod = computed<LoginMethod>(() => {
    if (!this.usersWithoutEmails()) {
      return LoginMethod.Email; // Legacy behavior
    }
    if (this.memberData()?.login && !this.memberData()?.hasWorkEmail) {
      return LoginMethod.Username;
    }
    return LoginMethod.Email;
  });

  public selectedMemberAbilities = computed<
    Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>
  >(() => this.memberData()?.abilities || []);

  public disableForm = effect(() => {
    this.memberData()?.status === TeamMemberStatus.Deactivated
      ? this.formGroup.disable()
      : this.formGroup.enable();
  });

  public formUpdate = effect(() => {
    const formControls = this.formGroup.controls.seats.controls;
    formControls.recruitee.setValue(
      this.updateSeatFormGroup(SeatKind.Recruitment, this.memberData()),
    );
    formControls.kiwi.setValue(this.updateSeatFormGroup(SeatKind.CoreHR, this.memberData()));
    this.adjustAccessAndRolesControls();
  });

  protected LoginMethod = LoginMethod;
  protected changeLoginInfoboxButton = computed<InfoboxButton[]>(() => {
    const member = this.memberData()!;

    return [
      {
        text: this.t('tlc.members.edit_member.permission_access_tab.infobox.cta_button'),
        actionFunction: () =>
          this.editMembersActionsService.changeLoginMethod(member.login!, member.guid!),
        variant: 'outline-informative',
      },
    ];
  });

  public ngOnInit() {
    this.appsDataFacade.fetchCollection();

    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid);

    this.formGroup = this.formBuilder.group<EditMemberForm>({
      seats: this.formBuilder.group({
        recruitee: this.createSeatFormGroup(SeatKind.Recruitment, this.memberData()),
        kiwi: this.createSeatFormGroup(SeatKind.CoreHR, this.memberData()),
      }),
    });
  }

  public ngAfterViewInit(): void {
    const seats = this.formGroup.controls.seats.controls;
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.Recruitment, seats.recruitee);
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.CoreHR, seats.kiwi);

    this.formGroup.controls.seats.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.adjustAccessAndRolesControls();
      });
  }

  public get seats() {
    return this.formGroup?.controls?.seats?.controls;
  }

  public submit() {
    const payload = this.editMemberAccessService.resolveSeatsPayload(
      this.memberData()!,
      this.formGroup,
      this.tellentApps()!,
    );

    if (payload.length) {
      this.pending.set(true);
      this.membersDataFacade.manageSeats({ seats: payload });

      race(this.membersDataFacade.manageSeatsSuccess$, this.membersDataFacade.manageSeatsFailed$)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(({ payload }) => {
          this.formGroup.markAsPristine();
          const result = { success: !(payload instanceof HttpErrorResponse), payload };
          this.dialogRef.close(result);
        });
    }
  }

  public cancel() {
    this.dialogRef.close();
  }

  public sendInvitation(): void {
    this.editMembersActionsService.startInviteExistingMemberFlow(this.dialogRef, this.memberGuid);
  }

  public openGiveAdminPermissionsDialog(): void {
    this.dialogService
      .openDialog(
        GiveAdminPermissionsDialogComponent,
        { abilities: this.selectedMemberAbilities() },
        { width: DialogWidth.Small, containerOptions: { fullscreenOnMobile: true } },
      )
      .afterClosed.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const guid = this.memberData()?.guid;
        if (result && guid)
          this.membersDataFacade.updateAbilities({ guid, abilities: result.abilities });
      });
  }

  private createSeatFormGroup(
    seatKind: SeatKind,
    memberData: TellentMember | null,
  ): FormGroup<SeatType> {
    const seat = memberData!.seats.find(seat => seat.app.kind === seatKind);
    const isMemberListedOnly = SeatKind.CoreHR && seat?.role === null;
    const defaultRole = this.appsDataFacade.defaultRoleBySeatKind(seatKind);

    const formGroup = this.formBuilder.group({
      toggle: [seat?.status === 'active'],
      appGuid: [seat?.app.guid!],
      roleExternalId: [
        !isMemberListedOnly
          ? (seat?.role?.externalId || defaultRole()?.externalId)!
          : MEMBER_LISTED_ONLY,
      ],
    });

    if (seat?.app.status === 'pending') {
      formGroup.disable({ emitEvent: false });
    }

    return formGroup;
  }

  private updateSeatFormGroup(seatKind: SeatKind, memberData: TellentMember | null): SeatTypeValue {
    const seat = memberData!.seats.find(seat => seat.app.kind === seatKind);
    const isMemberListedOnly = SeatKind.CoreHR && seat?.role === null;
    const defaultRole = this.appsDataFacade.defaultRoleBySeatKind(seatKind);

    const roleExternalId = seat
      ? !isMemberListedOnly
        ? (seat?.role?.externalId || defaultRole()?.externalId)!
        : MEMBER_LISTED_ONLY
      : null;

    return {
      toggle: seat?.status === 'active',
      appGuid: seat?.app.guid! || null,
      roleExternalId: roleExternalId,
    };
  }

  // When adding a member via username and password, we need to ensure that Recruitee seat and Kiwi Administrator role are disabled.
  // Only Kiwi seat should be enabled with Employee role.
  private adjustAccessAndRolesControls(): void {
    if (this.loginMethod() === LoginMethod.Username) {
      const kiwiValue = this.seats.kiwi.value;
      this.seats.recruitee.disable({ emitEvent: false });
      this.seats.recruitee.reset(
        { toggle: null, appGuid: '', roleExternalId: '' },
        { emitEvent: false },
      );
      this.seats.kiwi.reset({ ...kiwiValue, roleExternalId: EMPLOYEE_ROLE }, { emitEvent: false });
    } else {
      this.formGroup.controls.seats.controls.recruitee.enable({ emitEvent: false });
    }
  }
}
